import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useLang } from "../lib/lang-context";
import { ArrowUpRight, Clock } from "@phosphor-icons/react";
import Reveal from "./Reveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Blog() {
  const { t, lang } = useLang();
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/blog/posts`, { params: { lang } })
      .then((res) => setPosts(res.data.posts || []))
      .catch(() => setPosts([]));
  }, [lang]);

  return (
    <section
      id="blog"
      data-testid="blog-section"
      className="py-24 md:py-32 bg-slate-50 dark:bg-zinc-900 border-t border-slate-200 dark:border-zinc-800"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
          <Reveal className="lg:col-span-7">
            <p className="text-xs tracking-[0.24em] uppercase font-bold text-[var(--icor-blue)] eyebrow-line">
              {t.blog.eyebrow}
            </p>
            <h2 className="font-display text-4xl md:text-5xl font-semibold tracking-tighter mt-5 text-[var(--icor-navy)] dark:text-white text-balance">
              {t.blog.title}
            </h2>
            <p className="mt-5 text-lg text-slate-600 dark:text-zinc-400 leading-relaxed max-w-2xl">
              {t.blog.subtitle}
            </p>
          </Reveal>
          <Reveal delay={150} className="lg:col-span-5 lg:text-right">
            <Link
              to="/blog"
              data-testid="blog-view-all"
              className="inline-flex items-center gap-2 text-sm font-medium tracking-tight text-[var(--icor-blue)] hover:underline"
            >
              {t.blog.viewAll}
              <ArrowUpRight size={16} />
            </Link>
          </Reveal>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.map((p, i) => (
            <Reveal key={p.slug} delay={i * 80} className="h-full">
              <Link
                to={`/blog/${p.slug}`}
                data-testid={`blog-card-${p.slug}`}
                className="card-lift group bg-white dark:bg-zinc-950 border border-slate-200 dark:border-zinc-800 overflow-hidden flex flex-col h-full"
                style={{ borderRadius: 4 }}
              >
                <div className="relative aspect-[16/10] overflow-hidden bg-slate-100 dark:bg-zinc-800">
                  <img
                    src={p.image}
                    alt={p.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute top-3 left-3 text-[10px] tracking-[0.2em] uppercase font-bold text-white bg-black/70 backdrop-blur-sm px-2.5 py-1">
                    {p.tag}
                  </div>
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="font-display text-xl md:text-2xl font-medium tracking-tight text-[var(--icor-navy)] dark:text-white">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-sm text-slate-600 dark:text-zinc-400 leading-relaxed flex-1">
                    {p.excerpt}
                  </p>
                  <div className="mt-5 pt-5 border-t border-slate-100 dark:border-zinc-800 flex items-center justify-between text-xs uppercase tracking-[0.18em] font-semibold">
                    <span className="text-slate-500 dark:text-zinc-500">{p.date}</span>
                    <span className="flex items-center gap-1.5 text-slate-500 dark:text-zinc-500">
                      <Clock size={12} weight="bold" /> {p.read}
                    </span>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
